import { API_BASE_URL } from '../config/apiConfig';

const handleUnauthorized = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('adminData');
  window.location.href = '/login';
};

const authenticatedFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    if (response.status === 401 || response.status === 403) {
      handleUnauthorized();
      throw new Error('Session expired. Please login again.');
    }

    return response;
  } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const loginAPI = async (email, password) => {
  try {
    const loginUrl = `${API_BASE_URL}/auth/login`;

    const response = await authenticatedFetch(loginUrl, 
      {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });
    
    const contentType = response.headers.get('content-type'); 
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Server error: Expected JSON but received ${contentType || 'unknown content type'}. Please check if the API endpoint is correct.`);
    }
                        
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Login failed. Please check your credentials.');
    }
    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error(`Network error: Cannot reach ${API_BASE_URL}. Check if backend is running and CORS is configured.`);
    }
    if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
      throw new Error('Invalid response from server. Please check if the API URL is correct.');
    }
    throw error;
  }
};

export const getCustomersAPI = async (page = 1, limit = 10, search = '', status = 2) => {
  try {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      status: status.toString(),
      ...(search && { search })
    });

    const response = await authenticatedFetch(`${API_BASE_URL}/customers?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch customers data.');
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const changeCustomerStatusAPI = async (customerId, status) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/change-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to change customer status.');
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const updateCustomerProfileAPI = async (customerId, profileData) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(profileData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to update customer profile.');
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const getCustomerDetailsAPI = async (customerId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}`, 
      {                                                            
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch customer  details.');
    }
    return result;
  } catch (error) {
    throw error;
  }
};

export const updateCustomerAPI = async (customerId, customerData) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(customerData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to update customer.');   
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const getCustomerProjectsAPI = async (customerId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch customer projects.');
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const uploadCustomerImageAPI = async (customerId, imageFile) => {
  const fieldNames = ['profile_image', 'image', 'file', 'avatar'];
  
  for (const fieldName of fieldNames) {
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append(fieldName, imageFile); 

      const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 500) {
        continue;
      }

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      if (!response.ok) {
        if (response.status === 422 || response.status === 400) {
          continue;
        }
        throw new Error(result.message || result.error || `Server error: ${response.status} ${response.statusText}`);
      }
      return result;
    } catch (error) {
      console.error(`Upload failed with field name ${fieldName}:`, error);
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      if (fieldName === fieldNames[fieldNames.length - 1]) {
        throw error;
      }
    }
  }
  
  throw new Error('Failed to upload image with any field name');
};

export const getAdminProfileAPI = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/auth/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch admin profile.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const updateAdminProfileAPI = async (profileData) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/auth/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(profileData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to update admin profile.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const changeAdminPasswordAPI = async (passwordData) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/auth/change-password`, 
      {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(passwordData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to change password.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const uploadAdminImageAPI = async (imageFile) => {
  try {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('profile_image', imageFile);

    const response = await authenticatedFetch(`${API_BASE_URL}/auth/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to upload image.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getDashboardAPI = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/dashboard/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch dashboard data.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

// Add the getReportsAPI function here
export const getReportsAPI = async (status = null, startDate = null, endDate = null) => {
  try {
    const token = localStorage.getItem('authToken');

    // Build URL with query parameters
    let url = `${API_BASE_URL}/subscriptions/customers`;
    const params = new URLSearchParams();

    if (status) {
      params.append('status', status);
    }
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch reports data.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getDashboardReportsAPI = async (startDate, endDate) => {
  try {
    const token = localStorage.getItem('authToken');

    const response = await authenticatedFetch(`${API_BASE_URL}/dashboard/reports?start_date=${startDate}&end_date=${endDate}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch dashboard reports.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getSubscriptionsAPI = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/subscriptions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch subscriptions.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProjectDetailsAPI = async (customerId, projectId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch project details.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProjectProcurementsAPI = async (customerId, projectId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/procurements`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch procurements.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProcurementDetailsAPI = async (customerId, projectId, procurementId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/procurements/${procurementId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch procurement details.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProcurementDocumentsAPI = async (customerId, projectId, procurementId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/procurements/${procurementId}/documents`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch procurement documents.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getDocumentFoldersAPI = async (customerId, projectId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/document-folders`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch document folders.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getFolderDocumentsAPI = async (customerId, projectId, folderId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/document-folders/${folderId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch folder documents.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProjectTeamMembersAPI = async (customerId, projectId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/members`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch team members.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProjectCategoriesAPI = async (customerId, projectId) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/category`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch project categories.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getProjectSubCategoriesAPI = async (customerId, projectId, categoryName) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/project/${projectId}/category/${encodeURIComponent(categoryName)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch sub-categories.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const updateSubscriptionAPI = async (subscriptionId, subscriptionData) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await authenticatedFetch(`${API_BASE_URL}/subscriptions/${subscriptionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(subscriptionData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to update subscription.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getSubscriptionCustomersAPI = async (status = null) => {
  try {
    const token = localStorage.getItem('authToken');
    const url = status
      ? `${API_BASE_URL}/subscriptions/customers?status=${status}`
      : `${API_BASE_URL}/subscriptions/customers`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to fetch subscription customers.');
    }

    return result;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};